import { Suspense, type ComponentType, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { RequirePageAccess } from '@/app/require-page-access'
import { useQueryClient } from '@tanstack/react-query'
import type { Session } from '@supabase/supabase-js'

import { Lock } from 'lucide-react'
import { AppShell } from '@/components/layout/app-shell'
import { RouteFallback } from '@/components/layout/route-fallback'
import { Button } from '@/components/ui/button'
import { useCreatePortal } from '@/hooks/use-subscription'
import { useFreeAccess } from '@/hooks/use-free-access'

// Supabase auth hash'lerini React mount olmadan önce yakala ve yönlendir
// Bu kod modül yüklenirken çalışır - useEffect'ten önce
function handleAuthHashRedirect(): boolean {
  if (typeof window === 'undefined') return false

  const { hash, pathname } = window.location
  if (!hash || pathname !== '/') return false

  const hashParams = new URLSearchParams(hash.replace(/^#/, ''))
  const accessToken = hashParams.get('access_token')
  const refreshToken = hashParams.get('refresh_token')
  const type = hashParams.get('type')
  const errorCode = hashParams.get('error')

  // Recovery token'ları varsa reset-password'a yönlendir
  if (accessToken && refreshToken && type === 'recovery') {
    console.info('[App] Recovery hash detected on root, redirecting to /reset-password')
    window.location.replace(`/reset-password#${hash.slice(1)}`)
    return true
  }

  // Error varsa login'e yönlendir
  if (errorCode) {
    console.info('[App] Auth error hash detected on root, redirecting to /login')
    window.location.replace(`/login${hash}`)
    return true
  }

  return false
}

// Modül yüklenirken çalıştır
const isRedirecting = handleAuthHashRedirect()
import { appRoutes } from '@/app/routes'
import { Placeholder } from '@/pages/placeholder'
// #1123: 404 ekranı küçük ve StatusScreen zaten ana pakette (Placeholder) — tembel yüklenmez.
import { NotFoundPage } from '@/pages/not-found'
import {
  AliasPage,
  KaiPage,
  AuthPage,
  DashboardPage,
  DeepsearchPage,
  FreqFinderPage,
  LandingPage,
  AuthCallbackPage,
  ProfilePage,
  ResetPasswordPage,
  RankTrackerPage,
  SettingsPage,
  UnauthorizedPage,
  NewReleasesPage,
  SalesEstimatorPage,
  KeyInsightPage,
  ProductDatabasePage,
  AdminAllowedEmailsPage,
  AnchorReviewPage,
  UserActivityPage,
  AdminAcademyPage,
  AdminContentManagementPage,
  AdminBlogManagementPage,
  AdminNotificationsPage,
  AdminScrapingGatePage,
  AdminKeywordScoreLabPage,
  AdminExtensionFeaturesPage,
  AcademyPage,
  VideoDetailPage,
  AboutPage,
  BlogComingSoonPage,
  ContactPage,
  PrivacyPolicyPage,
  TermsPage,
  PricingPage,
  CheckoutPage,
  OrderDetailPage,
  ProfitCalculatorPage,
  KeywordListsPage,
  ListingBuilderPage,
  AmazonDashboardPage,
  AmazonOAuthCallbackPage,
  BillingPage,
  SQPPage,
  AdvertisingPage,
  ProductDetailPage,
  ProductListPage,
  SearchVolumePage,
} from '@/app/lazy-pages'
import { ALIAS_ROUTES } from '@/pages/alias-routes'
import { supabase, isSupabaseConfigured } from '@/lib/supabase-client'
import { clearAccessCache, fetchAccessStatus } from '@/lib/access-control'
import { useUserProfile } from '@/app/providers/user-profile-provider'
import { useSubscriptionContext } from '@/app/providers/subscription-provider'
import { SessionEnforcementProvider } from '@/app/providers/session-enforcement-provider'
import { UpgradePrompt } from '@/components/billing/upgrade-prompt'
import { EntitlementUnavailable } from '@/components/billing/entitlement-unavailable'
import { isE2EAuthBypassEnabled } from '@/lib/e2e-auth'

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const [status, setStatus] =
    useState<'loading' | 'authenticated' | 'unauthenticated' | 'blocked'>('loading')
  const [blockedInfo, setBlockedInfo] = useState<{ email: string; reason?: 'error' | 'unauthorized' } | null>(null)
  const queryClient = useQueryClient()
  const [bypassAuth] = useState(() => isE2EAuthBypassEnabled())
  // Son görülen kullanıcı kimliği: A çıkmadan B'nin oturumu gelirse önbellek temizlenir.
  const lastUserIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (bypassAuth) {
      setStatus('authenticated')
      return
    }
    if (!isSupabaseConfigured() || !supabase) {
      setStatus('unauthenticated')
      return
    }

    let isMounted = true
    const evaluateSession = async (session: Session | null) => {
      if (!isMounted) return
      if (!session) {
        setStatus('unauthenticated')
        setBlockedInfo(null)
        return
      }

      const email = session.user?.email?.toLowerCase() ?? null
      if (!email) {
        setStatus('unauthenticated')
        setBlockedInfo(null)
        return
      }

      const { allowed, error: accessError } = await fetchAccessStatus(email)
      if (!isMounted) return

      if (!allowed) {
        if (accessError) {
          // Geçici network/API hatası — kullanıcıyı oturumdan ATMA.
          // Son bilinen durumu koru, bir sonraki event'te tekrar denenecek.
          console.warn('[RequireAuth] Access check geçici hata, oturum korunuyor:', accessError)
          setStatus((prev) => {
            if (prev !== 'loading') return prev // mevcut durumu koru
            return 'authenticated'
          })
          setBlockedInfo(null)
          return
        }
        // Gerçek yetkisizlik (hata yok, kullanıcı allowed_emails'de değil)
        setBlockedInfo({ email, reason: 'unauthorized' })
        await supabase.auth.signOut()
        if (!isMounted) return
        setStatus('blocked')
        return
      }

      setBlockedInfo(null)
      setStatus('authenticated')
    }

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return
      // TOKEN_REFRESHED: Sadece token yenilendi, kullanıcının yetkisi değişmedi.
      // Access check tekrar yapmak gereksiz ve riskli (geçici hata → signOut zinciri).
      if (event === 'TOKEN_REFRESHED') {
        if (session) {
          setStatus('authenticated')
        }
        return
      }
      // Kullanıcı değişiminde stale yetki sonucunu önlemek için cache'i temizle.
      if (event === 'SIGNED_OUT') {
        clearAccessCache()
        // Çıkış düğmesi hemen /login'e yönlendirir; bu bileşen aşağıdaki durum
        // effect'i çalışmadan sökülebilir. Bu yüzden temizlik burada, senkron yapılır.
        queryClient.clear()
      }
      // Kimlik değişimi (A → B, arada çıkış olmadan): önceki kullanıcının tüm
      // sorgu verisi silinir. İlk yükleme (null → A) ve aynı kullanıcıda
      // yenileme/tekrar giriş temizlemez.
      const nextUserId = session?.user?.id ?? null
      const previousUserId = lastUserIdRef.current
      if (previousUserId && nextUserId && previousUserId !== nextUserId) {
        queryClient.clear()
      }
      lastUserIdRef.current = nextUserId
      // INITIAL_SESSION: Supabase storage'dan session restore etti
      // SIGNED_IN: Yeni login
      // SIGNED_OUT: Logout veya session expiry
      void evaluateSession(session)
    })

    return () => {
      isMounted = false
      listener.subscription.unsubscribe()
    }
  }, [bypassAuth, queryClient])

  useEffect(() => {
    if (bypassAuth) {
      return
    }
    if (status === 'unauthenticated' || status === 'blocked') {
      // Önceki kullanıcının hiçbir verisi (ana ekran, erişim kararı, P&L…) sonraki
      // oturuma kalmasın: tüm önbellek temizlenir.
      queryClient.clear()
    }
  }, [bypassAuth, queryClient, status])

  if (bypassAuth) {
    return <>{children}</>
  }

  if (status === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Oturum doğrulanıyor…
      </div>
    )
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />
  }

  if (status === 'blocked') {
    return <Navigate to="/unauthorized" replace state={blockedInfo ?? undefined} />
  }

  return <>{children}</>
}

export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { profile, isResolved } = useUserProfile()

  // Profil çözülene kadar `isAdmin=false` "admin değil" ANLAMINA GELMEZ — yalnız
  // "henüz bilinmiyor" demektir. Bu ayrım olmadan gerçek bir admin, korumalı bir
  // sayfayı yenilediğinde dashboard'a atılabilirdi (#492 review).
  if (!isResolved) return null

  if (!profile.isAdmin) {
    return <Navigate to="/app/dashboard" replace />
  }

  return <>{children}</>
}

export function RequirePro({
  children,
  label,
  path,
}: {
  children: React.ReactNode
  label: string
  /** #1029: sayfa yolu; sunucu "limited" modundayken Free'ye kısmen açık sayfaları ayırt eder. */
  path?: string
}) {
  const { profile } = useUserProfile()
  // #1029: sunucu "limited" modu yalnız Pro olmayana uygulanır. Kapalıyken (limited=false)
  // aşağıdaki akış bit-bit eskisi gibidir.
  const { limited, loading: quotaLoading, isOpenPath } = useFreeAccess()
  const { isPro, isLoading, entitlementUnknown, entitlementRetrying, isGraceExpired, isPaused } = useSubscriptionContext()
  const portalMutation = useCreatePortal()

  // Admin bypass — limited iken de AYNEN sürer: sunucuda `require_plan` yöneticiyi geçirir, sınırlı mod
  // yalnız Free'ye açılan uçlarda sayı sınırı uygular. Site sunucudan sıkı olursa yönetici kendi
  // kapalı sayfalarını (New Release, Akademi, P&L, KeyInsight...) kaybeder (#1029).
  if (profile.isAdmin) return <>{children}</>
  // Show loading while checking subscription
  if (isLoading) return null
  if (entitlementUnknown) return <EntitlementUnavailable retrying={entitlementRetrying} />

  // Grace period expired: show payment-update blocker instead of upgrade prompt
  if (isGraceExpired) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <div className="rounded-full border border-red-500/20 bg-red-500/10 p-4">
          <Lock className="h-8 w-8 text-red-400" />
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground">Aboneliğiniz Askıya Alındı</h3>
          <p className="mt-1 text-sm text-foreground/70">
            Son ödemeniz başarısız olduğu için erişiniz kısıtlandı. Ödeme yönteminizi güncelleyerek erişimi geri kazanın.
          </p>
        </div>
        <Button
          className="bg-red-500 text-white hover:bg-red-600"
          onClick={() => portalMutation.mutate()}
          disabled={portalMutation.isPending}
        >
          {portalMutation.isPending ? 'Yükleniyor...' : 'Ödeme Güncelle'}
        </Button>
      </div>
    )
  }

  // Paused: show full blocker with support + portal buttons
  if (isPaused) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <div className="rounded-full border border-red-500/20 bg-red-500/10 p-4">
          <Lock className="h-8 w-8 text-red-400" />
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-foreground">Hesabınız Durduruldu</h3>
          <p className="mt-1 text-sm text-foreground/70">
            Hesabınız geçici olarak durduruldu. Pro özellikleri kullanamıyor olabilirsiniz.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            asChild
          >
            <a href="/contact">Destek ile İletişime Geç</a>
          </Button>
          <Button
            className="bg-red-500 text-white hover:bg-red-600"
            onClick={() => portalMutation.mutate()}
            disabled={portalMutation.isPending}
          >
            {portalMutation.isPending ? 'Yükleniyor...' : 'Aboneliği Yönet'}
          </Button>
        </div>
      </div>
    )
  }

  // #1029: Pro olmayan kullanıcıda kota durumu gelene kadar bekle; yoksa UpgradePrompt bir an
  // görünüp sayfaya geçerdi (titreme). Askı/ödeme-süresi engelleri YUKARIDA kalır: kota ucu
  // yanıt vermese de onlar beklemeden görünür.
  if (!isPro && quotaLoading) return null
  // #1029: limited modda Free'ye kısmen açık sayfa (Pro olmayan yönetici olmayan kullanıcı)
  if (!isPro && limited && isOpenPath(path)) return <>{children}</>

  // Show upgrade prompt if not Pro
  if (!isPro) return <UpgradePrompt feature={label} />

  return <>{children}</>
}

const routeComponentMap: Record<string, ComponentType> = {
  '/app/chatbot': KaiPage,
  '/app/dashboard': DashboardPage,
  '/app/rank-tracker': RankTrackerPage,
  '/app/deepsearch': DeepsearchPage,
  '/app/freqfinder': FreqFinderPage,
  '/app/key-insight': KeyInsightPage,
  '/app/product-database': ProductDatabasePage,
  '/app/settings': SettingsPage,
  '/app/profile': ProfilePage,
  '/app/new-release': NewReleasesPage,
  '/app/sales-estimator': SalesEstimatorPage,
  '/app/profit-calculator': ProfitCalculatorPage,
  '/app/admin/allowed-emails': AdminAllowedEmailsPage,
  '/app/admin/anchor-review': AnchorReviewPage,
  '/app/admin/user-activity': UserActivityPage,
  '/app/admin/academy': AdminAcademyPage,
  '/app/admin/content': AdminContentManagementPage,
  '/app/admin/content/blog': AdminBlogManagementPage,
  '/app/admin/notifications': AdminNotificationsPage,
  '/app/admin/scraping-gate': AdminScrapingGatePage,
  '/app/admin/keyword-score-lab': AdminKeywordScoreLabPage,
  '/app/admin/extension-features': AdminExtensionFeaturesPage,
  '/app/academy': AcademyPage,
  '/app/keyword-lists': KeywordListsPage,
  '/app/listing-builder': ListingBuilderPage,
  '/app/amazon/dashboard': AmazonDashboardPage,
  '/app/amazon/advertising': AdvertisingPage,
  '/app/billing': BillingPage,
  '/app/sqp': SQPPage,
  '/app/product-list': ProductListPage,
}

const LoadingFallback = () => <RouteFallback />

export function App() {
  // Eğer auth hash yönlendirmesi yapılıyorsa, boş render et
  if (isRedirecting) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Yönlendiriliyor…
      </div>
    )
  }

  return (
    <SessionEnforcementProvider>
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/pricing/checkout" element={<CheckoutPage />} />
          <Route path="/coming-soon/blog" element={<BlogComingSoonPage />} />
          <Route path="/order-detail" element={<OrderDetailPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          {ALIAS_ROUTES.map((route) => (
            <Route key={route.path} path={route.path} element={<AliasPage route={route} />} />
          ))}
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/amazon/callback" element={<AmazonOAuthCallbackPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route
            path="/app"
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route index element={<DashboardPage />} />
            {appRoutes.map((route) => {
              const Component = routeComponentMap[route.path] ?? (() => (
                <Placeholder title={route.label} description={route.description} />
              ))

              const nestedPath = route.path.replace(/^\/app\/?/, '')

              // Wrap admin-only routes with RequireAdmin, pro-only with RequirePro
              // #825 dilim 3: merkezi erişim tablosu EN İÇTE (bugünkü kural önce işler; tablo yalnız kısıtlar).
              let element: React.ReactNode = (
                <RequirePageAccess route={route}>
                  <Component />
                </RequirePageAccess>
              )
              if (route.proOnly) {
                element = (
                  <RequirePro label={route.label} path={route.path}>
                    {element}
                  </RequirePro>
                )
              }
              if (route.adminOnly) {
                element = <RequireAdmin>{element}</RequireAdmin>
              }

              return <Route key={route.path} path={nestedPath} element={element} />
            })}
            <Route path="profile" element={<ProfilePage />} />
            {/* SV — admin-only iç araç. appRoutes'a EKLENMEZ (sidebar'da gizli). */}
            <Route
              path="sv"
              element={
                <RequireAdmin>
                  <SearchVolumePage />
                </RequireAdmin>
              }
            />
            <Route
              path="academy/:playlistId/:videoId"
              element={
                <RequirePro label="Akademi" path="/app/academy">
                  <VideoDetailPage />
                </RequirePro>
              }
            />
            <Route
              path="amazon/product/:asin"
              element={
                <RequireAdmin>
                  <ProductDetailPage />
                </RequireAdmin>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </SessionEnforcementProvider>
  )
}
